/*
 * Copyright (c) 2024. World Wide Generation Ltd
 */

import { useEffect, useMemo, useState } from 'react';
import { Loader } from '@g17eco/atoms/loader';
import Dashboard, { DashboardRow, DashboardSectionTitle } from '@g17eco/molecules/dashboard';
import { useAppDispatch, useAppSelector } from '@reducers/index';
import { useParams } from 'react-router';
import { AdminBreadcrumbs } from '@routes/admin-dashboard/AdminBreadcrumbs';
import { Button, TabContent, TabPane } from 'reactstrap';
import { Redirect, useHistory, useRouteMatch } from 'react-router-dom';
import { initiativeApi, useGetSurveyDeadlineStatsQuery, useGetUserStatsQuery } from '@api/initiative-stats';
import { loadInitiativeById } from '@actions/initiative';
import { OverviewProgressRow } from '@features/admin-dashboard/components/ProgressRow';
import { DeadlineReminders } from '@apps/company-tracker/components/admin-dashboard/DeadlineReminders';
import { toPercentageProgress } from './util';
import { isUserManagerByInitiativeId } from '@selectors/user';
import G17Client from '@services/G17Client';
import { generateUrl } from '@routes/util';
import { ROUTES } from '@constants/routes';
import { isSingleOrg, getCurrentInitiativeTree } from '@selectors/initiative';
import { FeaturePermissions } from '@services/permissions/FeaturePermissions';
import { SurveyStatsTable } from '@features/admin-dashboard/tables/SurveyStatsTable';
import { UserStatsTable } from '@features/admin-dashboard/tables/UserStatsTable';
import { getRootConfig } from '@selectors/globalData';
import { useTabs } from '@hooks/useTabs';
import { Menu } from '@g17eco/molecules/menu/Menu';
import { SurveyStatsFiltersProps } from '@features/admin-dashboard/types';
import { SurveyStatsFilters } from '@features/admin-dashboard/filters/SurveyStatsFilters';
import { loadSavedFilters, saveFilters } from '@features/admin-dashboard/utils/filterStorage';
import { useCurrentUser } from '@hooks/useCurrentUser';
import { SurveyDeadlineStats } from '@g17eco/types/initiativeStats';
import { AdminSectionTitle } from './AdminSectionTitle';
import { DEFAULT_FILTER_OPTIONS, type FilterOptionsMap } from '@features/admin-dashboard/filters/constants';
import { skipToken } from '@reduxjs/toolkit/query';

enum AdminDashboardTab {
  Surveys = 'surveys',
  Users = 'users',
}

interface RouteParams {
  initiativeId: string;
}

const OVERALL_PROGRESS =
  'Displays the overall progress of all filtered reports, showing totals for unanswered, answered, rejected, and verified — matching the breakdown shown below.';

const renderLabel = ({ name, level }: { name: string; level?: number }) => {
  return level !== undefined ? (
    <div className='d-flex w-100 justify-content-between'>
      <span className='text-truncate' style={{ maxWidth: '80%' }}>
        {name}
      </span>
      <span>Level: {level}</span>
    </div>
  ) : (
    name
  );
};

export const AdminOverview = () => {
  const { url } = useRouteMatch();
  const history = useHistory();
  const dispatch = useAppDispatch();

  const { initiativeId } = useParams<RouteParams>();
  const currentUser = useCurrentUser();

  const rootConfig = useAppSelector(getRootConfig);
  const limitReportingLevels = FeaturePermissions.getLimitReportingLevels(rootConfig);
  const isSingleOrganisation = useAppSelector(isSingleOrg);
  const initiativeState = useAppSelector((state) => state.initiative);
  const currentInitiativeTree = useAppSelector(getCurrentInitiativeTree);
  const query = useMemo(() => new URLSearchParams(history.location.search), [history.location.search]);
  const userId = query.get('userId');
  const isUserAdmin = useAppSelector((state) => isUserManagerByInitiativeId(state, initiativeId));

  const [filterValues, setFilterValues] = useState<SurveyStatsFiltersProps>(() =>
    loadSavedFilters({ userId: currentUser._id, initiativeId }),
  );

  const [filterOptions, setFilterOptions] = useState<FilterOptionsMap>(DEFAULT_FILTER_OPTIONS);

  const { isLoading, data: surveyListStats = [] } = useGetSurveyDeadlineStatsQuery(
    filterValues.initiatives
      ? {
          initiativeId,
          surveyStatuses: filterValues.surveyStatuses,
          initiativeIds: filterValues.initiatives,
          years: filterValues.years,
          months: filterValues.months,
          periods: filterValues.periods,
        }
      : skipToken,
  );

  const { data: userListStats = [], isLoading: isUserStatsLoading, error } = useGetUserStatsQuery(
    filterValues.initiatives
      ? {
          initiativeId,
          surveyStatuses: filterValues.surveyStatuses,
          initiativeIds: filterValues.initiatives,
          years: filterValues.years,
          months: filterValues.months,
          periods: filterValues.periods,
        }
      : skipToken,
  );

  const { currentTab, handleChangeTab } = useTabs({ initialTab: AdminDashboardTab.Surveys });

  const handleReload = async () => {
    dispatch(initiativeApi.util.invalidateTags(['stats']));
  };

  useEffect(() => {
    dispatch(loadInitiativeById(initiativeId));
  }, [dispatch, initiativeId]);

  useEffect(() => {
    setFilterOptions((prev: FilterOptionsMap) => ({
      ...prev,
      initiatives: currentInitiativeTree.map((initiative) => ({
        value: initiative._id,
        label: renderLabel(initiative),
        searchString: initiative.name,
      })),
    }));
  }, [currentInitiativeTree]);

  const surveyStats: SurveyDeadlineStats[] = useMemo(() => {
    return surveyListStats.reduce<SurveyDeadlineStats[]>((acc, current) => {
      acc.push(...current.surveys);
      return acc;
    }, []);
  }, [surveyListStats]);

  // Multi-select change handler
  const handleMultiSelectChange = (data: Partial<SurveyStatsFiltersProps>) => {
    setFilterValues((prev) => ({ ...prev, ...data }));
  };

  // Manual save handler - called when user clicks "Save Filters" button
  const handleSaveFilters = () => {
    saveFilters({ userId: currentUser._id, initiativeId, filters: filterValues });
  };

  if (!initiativeState.loaded || isLoading || isUserStatsLoading) {
    return <Loader />;
  }

  if (limitReportingLevels === 1 && isSingleOrganisation) {
    const redirectUrl = generateUrl(ROUTES.ADMIN_SETTINGS, {
      initiativeId: initiativeId,
    });
    return <Redirect to={redirectUrl} />;
  }

  const initiative = initiativeState.data;
  const initiativeStatuses = surveyListStats.find((i) => i._id === initiativeId)?.status;
  const progress = initiativeStatuses ? toPercentageProgress(initiativeStatuses) : undefined;

  const handleDownload = () => {
    G17Client.downloadManagerInitiativeStats(initiative._id);
  };

  return (
    <div className='admin-dashboard'>
      <Dashboard>
        <DashboardRow>
          <AdminBreadcrumbs breadcrumbs={[{ label: 'Analytics Dashboard' }]} initiativeId={initiativeId} />
        </DashboardRow>

        <DashboardSectionTitle
          title={
            <>
              Analytics dashboard: Overview
              <Button color='link' className='ml-2'>
                <i className='fas fa-file-excel' onClick={() => handleDownload()}></i>
              </Button>{' '}
            </>
          }
        />

        <Menu
          className='px-3 mb-4'
          items={[
            {
              label: 'Report progress',
              onClick: () => handleChangeTab(AdminDashboardTab.Surveys),
              active: currentTab === AdminDashboardTab.Surveys,
            },
            {
              label: 'User progress',
              onClick: () => handleChangeTab(AdminDashboardTab.Users),
              active: currentTab === AdminDashboardTab.Users,
            },
          ]}
        />

        <SurveyStatsFilters
          filterOptions={filterOptions}
          filters={filterValues}
          onMultiSelectChange={handleMultiSelectChange}
          onSaveFilters={handleSaveFilters}
        />

        <TabContent activeTab={currentTab}>
          <TabPane tabId={AdminDashboardTab.Surveys}>
            {progress ? (
              <OverviewProgressRow
                title={<AdminSectionTitle title='Overall organisational progress' tooltip={OVERALL_PROGRESS} />}
                progress={progress}
                style={{ height: '2rem' }}
                displayValues={false}
              />
            ) : null}
            <DeadlineReminders surveyStats={surveyStats} />
            <SurveyStatsTable
              initiativeId={initiativeId}
              isUserAdmin={isUserAdmin}
              handleReload={handleReload}
              url={url}
              surveyStats={surveyStats}
            />
          </TabPane>
          <TabPane tabId={AdminDashboardTab.Users}>
            <UserStatsTable
              initiativeId={initiativeId}
              userId={userId}
              url={url}
              userListStats={userListStats}
              error={error}
            />
          </TabPane>
        </TabContent>
      </Dashboard>
    </div>
  );
};
